import { useForm, Controller, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useContext, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Message } from 'semantic-ui-react';
import { Form } from 'components/form-elements/Form';
import { LabeledInput } from 'components/form-elements/inputs/LabeledInput';
import { Brikk } from 'components/Brikk';
import { ShouldRender } from 'components/ShouldRender';
import { useInviteUser } from 'components/../services/admin/invite-user';
import { adminUserSchema } from 'components/../utils/validation/schemas';
import { PENDING_TAB } from '../../../../constants';
import { UserAdministrationModalContext } from '../../../../UserAdministrationModalContext';
import { FormActions } from './FormActions';
import styles from './styles.module.scss';

export const AddUserForm = () => {

  const { isAddingUser, setIsAddingUser, setActiveTab, setInvitedUser, setHasUnsavedChanges } = useContext(UserAdministrationModalContext);
  const { inviteUser, isLoading } = useInviteUser({
    onSuccess: (data) => {
      setInvitedUser(data?.results[0]?.email);
      setIsAddingUser(false);
      setActiveTab(PENDING_TAB);
      setHasUnsavedChanges(false);
    },
  });

  const formMethods = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(adminUserSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      mobilephone: '',
      workphone: '',
      objectId: null,
      address: '',
      addressId: '',
      postalCode: '',
    },
  });

  const {
    control,
    formState: { errors },
  } = formMethods;

  const onSubmit = async (data) => {
    await inviteUser({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      mobilephone: data.mobilephone,
      workphone: data.workphone,
      objectId: data.objectId,
      address: data.address,
      addressId: data.addressId,
      postalCode: data.postalCode,
    });
  };

  useEffect(() => {
    if (formMethods.formState.isDirty) {
      setHasUnsavedChanges(formMethods.formState.isDirty);
    }
  }, [formMethods.formState.isDirty]);

  return (
    <div
      className={styles.formContainer}
    >
      <FormProvider {...formMethods}>
        <Brikk.Heading
          heading="addNewUser"
        />

        <Form
          id="add-user-form"
          className={styles.form}
        >

          <Controller
            name="firstName"
            control={control}
            render={({ field: { ref, onChange, value } }) => (
              <LabeledInput
                labelIntl="firstName"
                id="first-name"
                value={value}
                inputRef={ref}
                onChange={onChange}
                hasError={!!errors.firstName}
              />
            )}
          />

          <ShouldRender condition={!!errors.firstName}>
            <Message negative>
              <Message.Content>
                <FormattedMessage
                  id={errors.firstName ? errors.firstName.message : ''}
                  defaultMessage=""
                />
              </Message.Content>
            </Message>
          </ShouldRender>

          <Controller
            name="lastName"
            control={control}
            render={({ field: { ref, onChange, value } }) => (
              <LabeledInput
                labelIntl="lastName"
                id="family-name"
                value={value}
                inputRef={ref}
                onChange={onChange}
                hasError={!!errors.lastName}
              />
            )}
          />

          <ShouldRender condition={!!errors.lastName}>
            <Message negative>
              <Message.Content>
                <FormattedMessage
                  id={errors.lastName ? errors.lastName.message : ''}
                  defaultMessage=""
                />
              </Message.Content>
            </Message>
          </ShouldRender>

          <Controller
            name="email"
            control={control}
            render={({ field: { ref, onChange, value } }) => (
              <LabeledInput
                labelIntl="email"
                id="email"
                type="email"
                value={value}
                inputRef={ref}
                onChange={onChange}
                hasError={!!errors.email}
              />
            )}
          />

          <ShouldRender condition={!!errors.email}>
            <Message negative>
              <Message.Content>
                <FormattedMessage
                  id={errors.email ? errors.email.message : ''}
                  defaultMessage=""
                />
              </Message.Content>
            </Message>
          </ShouldRender>

          <ShouldRender condition={!!errors.phone}>
            <Message negative>
              <Message.Content>
                <FormattedMessage
                  id={errors.phone ? errors.phone.message : ''}
                  defaultMessage=""
                />
              </Message.Content>
            </Message>
          </ShouldRender>

          <Controller
            name="mobilephone"
            control={control}
            render={({ field: { ref, onChange, value } }) => (
              <LabeledInput
                labelIntl="mobilePhone"
                id="mobilePhone"
                value={value}
                inputRef={ref}
                onChange={onChange}
                hasError={!!errors.mobilePhone}
              />
            )}
          />

          <ShouldRender condition={!!errors.mobilePhone}>
            <Message negative>
              <Message.Content>
                <FormattedMessage
                  id={errors.mobilePhone ? errors.mobilePhone.message : ''}
                  defaultMessage=""
                />
              </Message.Content>
            </Message>
          </ShouldRender>

          <Controller
            name="workphone"
            control={control}
            render={({ field: { ref, onChange, value } }) => (
              <LabeledInput
                labelIntl="phoneWork"
                id="workPhone"
                value={value}
                inputRef={ref}
                onChange={onChange}
                hasError={!!errors.workPhone}
              />
            )}
          />

          <ShouldRender condition={!!errors.workPhone}>
            <Message negative>
              <Message.Content>
                <FormattedMessage
                  id={errors.workPhone ? errors.workPhone.message : ''}
                  defaultMessage=""
                />
              </Message.Content>
            </Message>
          </ShouldRender>

          <Controller
            name="phone"
            control={control}
            render={({ field: { ref, onChange, value } }) => (
              <LabeledInput
                labelIntl="phoneHome"
                id="homePhone"
                value={value}
                inputRef={ref}
                onChange={onChange}
                hasError={!!errors.phone}
              />
            )}
          />

          <ShouldRender condition={!!errors.phone}>
            <Message negative>
              <Message.Content>
                <FormattedMessage
                  id={errors.phone ? errors.phone.message : ''}
                  defaultMessage=""
                />
              </Message.Content>
            </Message>
          </ShouldRender>

          <Controller
            name="objectId"
            control={control}
            render={({ field: { ref, onChange, value } }) => (
              <LabeledInput
                labelIntl="aptNo"
                id="apartmentNumber"
                value={value}
                inputRef={ref}
                onChange={onChange}
                hasError={!!errors.objectId}
              />
            )}
          />

          <ShouldRender condition={!!errors.objectId}>
            <Message negative>
              <Message.Content>
                <FormattedMessage
                  id={errors.objectId ? errors.objectId.message : ''}
                  defaultMessage=""
                />
              </Message.Content>
            </Message>
          </ShouldRender>

          <Controller
            name="address"
            control={control}
            render={({ field: { ref, onChange, value } }) => (
              <LabeledInput
                labelIntl="address"
                id="address"
                value={value}
                inputRef={ref}
                onChange={onChange}
                hasError={!!errors.address}
              />
            )}
          />

          <ShouldRender condition={!!errors.address}>
            <Message negative>
              <Message.Content>
                <FormattedMessage
                  id={errors.address ? errors.address.message : ''}
                  defaultMessage=""
                />
              </Message.Content>
            </Message>
          </ShouldRender>

          <Controller
            name="addressId"
            control={control}
            render={({ field: { ref, onChange, value } }) => (
              <LabeledInput
                labelIntl="addressId"
                id="addressId"
                value={value}
                inputRef={ref}
                onChange={onChange}
                hasError={!!errors.addressId}
              />
            )}
          />

          <ShouldRender condition={!!errors.addressId}>
            <Message negative>
              <Message.Content>
                <FormattedMessage
                  id={errors.addressId ? errors.addressId.message : ''}
                  defaultMessage=""
                />
              </Message.Content>
            </Message>
          </ShouldRender>

          <Controller
            name="postalCode"
            control={control}
            render={({ field: { ref, onChange, value } }) => (
              <LabeledInput
                labelIntl="postalCode"
                id="postalCode"
                value={value}
                inputRef={ref}
                onChange={onChange}
                hasError={!!errors.postalCode}
              />
            )}
          />

          <ShouldRender condition={!!errors.postalCode}>
            <Message negative>
              <Message.Content>
                <FormattedMessage
                  id={errors.postalCode ? errors.postalCode.message : ''}
                  defaultMessage=""
                />
              </Message.Content>
            </Message>
          </ShouldRender>
        </Form>
        <ShouldRender condition={isAddingUser && Object.keys(errors).length > 0}>
          <Message negative>
            <Message.Content>
              <FormattedMessage
                id="formValidationError"
                defaultMessage="Some required information is missing. Please review and complete the form"
              />
            </Message.Content>
          </Message>
        </ShouldRender>
        <FormActions
          isLoading={isLoading}
          onSubmit={onSubmit}
        />
      </FormProvider>
    </div>
  );
};
